import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
import ErrorBoundary from "./Components/common/Errorboundary.js";
import ErrorPage from "./Components/common/ErrorPage.js";
import GlobalLoading from "./Components/common/GlobalLoading.js";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { isAuthenticated } from "./utils/auth";
import { useDisableActions } from "./utils/disableActions";
import "./utils/axiosConfig";
// Styles
import "./assests/css/Common.css";
import "./assests/css/common_modal.css";
import "./App.css";
import "./assests/css/theme.css";

// Layout & Context
import Sidebar from "./Components/Sidebar/Sidebar";
import Navbar from "./Components/Navbar/Navbar";
import { PageTitleProvider } from "./context/PageTitleContext";
import { ThemeProvider } from "./context/ThemeContext";
import { PermissionProvider } from "./context/PermissionContext";

// Authentication
import Login from "./Components/Authentication/Login";
import CarpenterProfile from "./Components/pages/Craftsmen/CarpenterProfile.js";

// Dashboard
const Dashboard = React.lazy(
  () => import("./Components/pages/Dashboard/Dashboard"),
);

// Master Module
const Users = React.lazy(() => import("./Components/pages/Master/Users"));
const Branch = React.lazy(() => import("./Components/pages/Master/Branch"));
const Department = React.lazy(
  () => import("./Components/pages/Master/Department"),
);
const EmployeeRole = React.lazy(
  () => import("./Components/pages/Master/EmployeeRole"),
);
const ShiftManagement = React.lazy(
  () => import("./Components/pages/Master/Shift"),
);
const Roles = React.lazy(() => import("./Components/pages/Master/Roles"));
const AddEditRole = React.lazy(
  () => import("./Components/pages/Master/Add_EditRole"),
);
const Brand = React.lazy(() => import("./Components/pages/Master/Brand"));
const AddBrand = React.lazy(
  () => import("./Components/pages/Master/Add_EditBrand"),
);
const Colors = React.lazy(() => import("./Components/pages/Master/Colors.js"));
const DocumentManager = React.lazy(
  () => import("./Components/pages/Master/DocumentManager.js"),
);
const Reminder = React.lazy(
  () => import("./Components/pages/Master/Reminder.js"),
);
const Catalog = React.lazy(
  () => import("./Components/pages/Master/Catalog.js"),
);

// Leads
const NewEnquiry = React.lazy(
  () => import("./Components/pages/Leads/NewEnquiries"),
);
const FollowUp = React.lazy(() => import("./Components/pages/Leads/FollowUps"));
const SiteVisit = React.lazy(
  () => import("./Components/pages/Leads/SiteVisit"),
);
const QuotationSent = React.lazy(
  () => import("./Components/pages/Leads/Quotation"),
);
const QuotationInvoice = React.lazy(
  () => import("./Components/pages/Leads/QuotationInvoice"),
);
const Enquirydashboard = React.lazy(
  () => import("./Components/pages/Leads/Enquirydashboard.js"),
);
const Future = React.lazy(() => import("./Components/pages/Leads/Future.js"));
const EstimatePage = React.lazy(
  () => import("./Components/pages/Leads/EstimatePage.js"),
);
const SiteDesigner = React.lazy(
  () => import("./Components/pages/Leads/SiteDesigner.js"),
);

// HR
const EmployeeMaster = React.lazy(
  () => import("./Components/pages/HR/EmployeeMaster"),
);
const AddEmployee = React.lazy(
  () => import("./Components/pages/HR/AddEmployee"),
);
const Attendance = React.lazy(
  () => import("./Components/pages/HR/Attendances"),
);
const LeaveManagement = React.lazy(
  () => import("./Components/pages/HR/LeaveManagement"),
);
const TaskManagement = React.lazy(
  () => import("./Components/pages/TaskManagement/TaskManagement.js"),
);
const SalaryManagement = React.lazy(
  () => import("./Components/pages/HR/SalaryManagement"),
);
const GenerateSalary = React.lazy(
  () => import("./Components/pages/HR/GenerateSalary"),
);
const AttendanceCalendar = React.lazy(
  () => import("./Components/pages/HR/AttendanceDetails"),
);
const EmployeeProfile = React.lazy(
  () => import("./Components/pages/HR/EmployeeProfile"),
);
const AttendanceDetails = React.lazy(
  () => import("./Components/pages/HR/AttendanceDetails"),
);
const AllowanceCharge = React.lazy(
  () => import("./Components/pages/HR/AllowanceCharge"),
);
const TravelAllowance = React.lazy(
  () => import("./Components/pages/HR/Allowance"),
);
const EmployeeAdvance = React.lazy(
  () => import("./Components/pages/HR/EmployeeAdvance.js"),
);
const EmployeeAdvanceDetailView = React.lazy(
  () => import("./Components/pages/HR/EmployeeAdvanceDetails.js"),
);
const Holiday = React.lazy(() => import("./Components/pages/HR/holiday.js"));
const PayslipRequests = React.lazy(
  () => import("./Components/pages/HR/PayslipRequests.js"),
);
const EmployeePayslip = React.lazy(
  () => import("./Components/pages/HR/EmployeePayslip.js"),
);
const IDCardPage = React.lazy(() => import("./Components/pages/HR/IDCard.js"));

// Clients & Projects
const Clients = React.lazy(() => import("./Components/pages/Clients/Clients"));
const ClientDetails = React.lazy(
  () => import("./Components/pages/Clients/ClientDetails"),
);
const Projects = React.lazy(
  () => import("./Components/pages/Projects/Projects"),
);
const AddProject = React.lazy(
  () => import("./Components/pages/Projects/AddProjects"),
);
const ViewProject = React.lazy(
  () => import("./Components/pages/Projects/Projects"),
);
const TrackProject = React.lazy(
  () => import("./Components/pages/Projects/Trackproject"),
);

// Inventory
const Product = React.lazy(
  () => import("./Components/pages/Inventory/Product"),
);
const AddProduct = React.lazy(
  () => import("./Components/pages/Inventory/AddProduct"),
);
const Purchase = React.lazy(
  () => import("./Components/pages/Inventory/Purchase"),
);
const Stock = React.lazy(() => import("./Components/pages/Inventory/Stock"));
const Damage = React.lazy(() => import("./Components/pages/Inventory/Damage"));
const AddPurchase = React.lazy(
  () => import("./Components/pages/Inventory/AddPurchase.js"),
);
const PurchaseReturn = React.lazy(
  () => import("./Components/pages/Inventory/PurchaseReturn.js"),
);
const AddPurchaseReturn = React.lazy(
  () => import("./Components/pages/Inventory/AddPurchaseReturn.js"),
);
const PurchaseReturnDetails = React.lazy(
  () => import("./Components/pages/Inventory/PurchaseReturnDetails.js"),
);
const OrderDetail = React.lazy(
  () => import("./Components/pages/Inventory/OrderDetail.js"),
);
const OrdersList = React.lazy(
  () => import("./Components/pages/Inventory/OrdersList"),
);
const StockReport = React.lazy(
  () => import("./Components/pages/Inventory/StockReport.js"),
);
const DailyStockPage = React.lazy(
  () => import("./Components/pages/Inventory/DailyStockPage.js"),
);

// Craftsmen
const CarpenterMaster = React.lazy(
  () => import("./Components/pages/Craftsmen/CarpenterMaster"),
);
const CarpenterMaterialIssue = React.lazy(() =>
  import("./Components/pages/Craftsmen/CarpenterMaterialIssue"),
);
const AddCarpenter = React.lazy(
  () => import("./Components/pages/Craftsmen/AddCarpenter"),
);
const Attendances = React.lazy(
  () => import("./Components/pages/Craftsmen/Attendances"),
);
const JobAssigned = React.lazy(
  () => import("./Components/pages/Craftsmen/JobAssigned"),
);
const WorkCompletion = React.lazy(
  () => import("./Components/pages/Craftsmen/WorkCompletion"),
);
const CarpenterAdvanceProfessional = React.lazy(
  () => import("./Components/pages/Craftsmen/Carpenteradvance"),
);
const CarpenterAdvanceDetailView = React.lazy(
  () => import("./Components/pages/Craftsmen/AdvanceDetails"),
);
const CarpenterSalaryManagement = React.lazy(
  () => import("./Components/pages/Craftsmen/CarpenterSalaryManagement"),
);
const GenerateCarpenterSalary = React.lazy(
  () => import("./Components/pages/Craftsmen/CarpenterSalaryGeneration"),
);
const Agents = React.lazy(
  () => import("./Components/pages/Craftsmen/Agents.js"),
);
const AgentView = React.lazy(
  () => import("./Components/pages/Craftsmen/AgentView.js"),
);

// Distributor
const Distributor = React.lazy(
  () => import("./Components/pages/Distributor/Distributor"),
);
const AddDistributor = React.lazy(
  () => import("./Components/pages/Distributor/AddDistributor"),
);
const ViewDistributor = React.lazy(
  () => import("./Components/pages/Distributor/ViewDistributor"),
);
const DistributorOrderHistory = React.lazy(
  () => import("./Components/pages/Distributor/DistributorOrderHistory"),
);
const Addorder = React.lazy(
  () => import("./Components/pages/Distributor/Addorder.js"),
);
// Trader
const Trader = React.lazy(
  () => import("./Components/pages/Distributor/Trader.js"),
);
const AddTrader = React.lazy(
  () => import("./Components/pages/Distributor/AddTrader.js"),
);
const ViewTrader = React.lazy(
  () => import("./Components/pages/Distributor/ViewTrader.js"),
);

// Billing (formerly Sales)
const Billing = React.lazy(
  () => import("./Components/pages/Billing/Billing.js"),
);
const AddBilling = React.lazy(
  () => import("./Components/pages/Billing/AddBilling.js"),
);
const BillingInvoice = React.lazy(
  () => import("./Components/pages/Billing/BillingInvoice.js"),
);
const Reports = React.lazy(() => import("./Components/pages/Reports/Reports"));
const PendingBilling = React.lazy(
  () => import("./Components/pages/Billing/PendingBilling.js"),
);
const BalanceSheet = React.lazy(
  () => import("./Components/pages/Billing/BalanceSheet.js"),
);
const SalesLedger = React.lazy(
  () => import("./Components/pages/Billing/SalesLedger.js"),
);
const ClientSalesHistory = React.lazy(
  () => import("./Components/pages/Billing/ClientSalesHistory.js"),
);
const SaleReturn = React.lazy(
  () => import("./Components/pages/Billing/SaleReturn.js"),
);
const AddSaleReturn = React.lazy(
  () => import("./Components/pages/Billing/AddSaleReturn.js"),
);
const SaleReturnDetails = React.lazy(
  () => import("./Components/pages/Billing/SaleReturnDetails.js"),
);
const ClientPaymentHistoryDetails = React.lazy(
  () => import("./Components/pages/Billing/ClientPaymentHistoryDetails.js"),
);

// Others
const Greetings = React.lazy(
  () => import("./Components/pages/Greetings/Greetings.js"),
);
const DigitalMarketing = React.lazy(
  () => import("./Components/pages/DigitalMarketing/DigitalMarketing.js"),
);
const HikvisionCamerasPage = React.lazy(
  () => import("./Components/pages/CCTVCAMERA/CCTVCamera.js"),
);
const ProductTracking = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Vehicle/ProductTracking"),
);
const Vehicles = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Vehicle/VehicleDetails"),
);
const AddVehicle = React.lazy(
  () => import("./Components/pages/Transport Management/Vehicle/AddVehicle"),
);
const VehicleView = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Vehicle/VehicleView.js"),
);
const ServiceHistory = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Vehicle/ServiceHistory"),
);
const AddEditServiceRecord = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Vehicle/AddEditServices"),
);
const DriverList = React.lazy(
  () => import("./Components/pages/Transport Management/Drivers/DriverList.js"),
);
// ✅ Item #22: Mss Name master
const MssNameList = React.lazy(
  () => import("./Components/pages/Transport Management/MssName/MssNameList.js"),
);
const AddDriver = React.lazy(
  () => import("./Components/pages/Transport Management/Drivers/AddDriver"),
);
const DriverSalary = React.lazy(
  () => import("./Components/pages/Transport Management/Drivers/DriverSalary"),
);
const DriverAdvance = React.lazy(
  () => import("./Components/pages/Transport Management/Drivers/DriverAdvance"),
);
// ✅ Payment-history UI standardization — matches Employee/Carpenter Advance
const DriverAdvanceDetailView = React.lazy(
  () =>
    import(
      "./Components/pages/Transport Management/Drivers/DriverAdvanceDetails"
    ),
);
const GenerateDriverSalary = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Drivers/GenerateDriverSalary"),
);
const LocationList = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Locations/LocationList"),
);
const AddLocation = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Locations/AddLocations"),
);
const ShipmentList = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Shipments/ShipmentList"),
);
const CreateShipment = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Shipments/CreateShipment"),
);
const ShipmentHistory = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Shipments/ShipmentHistory"),
);
const TripStart = React.lazy(
  () => import("./Components/pages/Transport Management/Shipments/TripStart"),
);
const LiveTracking = React.lazy(
  () =>
    import("./Components/pages/Transport Management/Shipments/LiveTracking"),
);
const AssetManagement = React.lazy(
  () => import("./Components/pages/Assets/Assets"),
);
const ExpenseManagement = React.lazy(
  () => import("./Components/pages/Expenses/Expenses"),
);
const ExpenseType = React.lazy(
  () => import("./Components/pages/Expenses/ExpenseType"),
);
const Warehouse = React.lazy(
  () => import("./Components/pages/Warehouse/Warehouse"),
);
const PosterEditor = React.lazy(
  () => import("./Components/pages/Greetings/PosterEditor.js"),
);

// Tally
const TallySync = React.lazy(
  () => import("./Components/pages/Tally/TallySync"),
);

// ── Auth check ────────────────────────────────────────────────────

// ── Protected layout ──────────────────────────────────────────────
function ProtectedLayout() {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-area">
        <Navbar />
        <div className="page-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

//////////////////////////////////////Disable Options //////////////////////////////////////////////

// Resets the error boundary whenever the route changes, so navigating
// away from a page that crashed gives the next page a clean render
// instead of staying stuck showing the old error.
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
  );
}

function App() {
  // Disables right-click, copy/cut/paste, text/image selection, DevTools
  // shortcuts + open-detection, and adds a watermark + screenshot/tab-blur
  // mitigation across the WHOLE app, since App.js is the root that wraps
  // every page/route. See src/utils/disableActions.js for all options.
  // useDisableActions({
  //   // Pages where Ctrl/Cmd+P should keep working normally (your Print
  //   // buttons on these pages already worked either way — this just also
  //   // lets users print via keyboard shortcut on these specific pages).
  // printExemptPaths: [
  //   "/billing/invoice",
  //   "/employeepayslip",
  //   "/hr/id-card",
  // ],
  //   // Replace "" with the logged-in user's name/email if you have it
  //   // available here (e.g. from your auth context), so any leaked
  //   // screenshot is traceable back to that user.
  //   watermark: { enabled: true, text: "" },
  // });

  return (
    <ThemeProvider>
      <Router>
        <PageTitleProvider>
          <PermissionProvider>
            <RouteErrorBoundary>
              <Suspense fallback={<GlobalLoading />}>
                <Routes>
                {/* ── Public Routes ── */}
                <Route path="/login" element={<Login />} />

                {/* ── Root Redirect ── */}
                {/* ✅ Item #20 fix: this used to unconditionally send
                    everyone to /login, even an already-authenticated user
                    who lands on "/" (e.g. via browser back-navigation all
                    the way to the first history entry, or any link that
                    points at the site root). That looked like "getting
                    logged out" and could also explain landing on a random
                    page instead of the Dashboard after logging back in. */}
                <Route
                  path="/"
                  element={
                    isAuthenticated() ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* ── Protected Routes ── */}
                <Route element={<ProtectedLayout />}>
                  {/* Dashboard */}
                  <Route path="/dashboard" element={<Dashboard />} />

                  {/* ── Master Module ── */}
                  <Route path="master">
                    <Route index element={<Navigate to="users" replace />} />
                    <Route path="roles" element={<Roles />} />
                    <Route path="users" element={<Users />} />
                    <Route path="branch" element={<Branch />} />
                    <Route path="department" element={<Department />} />
                    <Route path="employee-role" element={<EmployeeRole />} />
                    <Route path="shift" element={<ShiftManagement />} />
                    <Route path="brand" element={<Brand />} />
                    <Route path="colors" element={<Colors />} />
                    <Route
                      path="documentmanager"
                      element={<DocumentManager />}
                    />
                    <Route path="reminders" element={<Reminder />} />
                  </Route>

                  {/* Master - Roles Add/Edit */}
                  <Route path="roles/add" element={<AddEditRole />} />
                  <Route path="roles/edit/:id" element={<AddEditRole />} />

                  {/* Master - Brand Add/Edit */}
                  <Route path="brand/add" element={<AddBrand />} />
                  <Route path="brand/edit/:id" element={<AddBrand />} />
                  <Route path="catalog" element={<Catalog />} />

                  {/* ── Leads Module ── */}
                  <Route path="leads">
                    <Route
                      index
                      element={<Navigate to="new-enquiry" replace />}
                    />
                    <Route
                      path="enquiry-dashboard"
                      element={<Enquirydashboard />}
                    />
                    <Route path="enquiry-list" element={<Enquirydashboard />} />
                    <Route path="new-enquiry" element={<NewEnquiry />} />
                    <Route path="follow-up" element={<FollowUp />} />
                    <Route path="site-visit" element={<SiteVisit />} />
                    <Route path="quotation-sent" element={<QuotationSent />} />
                    <Route path="future" element={<Future />} />
                    <Route path="estimate" element={<EstimatePage />} />
                    <Route
                      path="site-designer"
                      element={<SiteDesigner />}
                    />
                  </Route>
                  <Route
                    path="quotation-invoice"
                    element={<QuotationInvoice />}
                  />

                  {/* ── HR Module ── */}
                  <Route path="hr">
                    <Route
                      index
                      element={<Navigate to="employee-master" replace />}
                    />
                    <Route
                      path="employee-master"
                      element={<EmployeeMaster />}
                    />
                    <Route
                      path="employee-master/add"
                      element={<AddEmployee />}
                    />
                    <Route
                      path="employee-master/edit/:id"
                      element={<AddEmployee />}
                    />
                    <Route
                      path="employee-master/view/:id"
                      element={<EmployeeProfile />}
                    />
                    <Route path="attendances" element={<Attendance />} />
                    <Route path="allowance" element={<TravelAllowance />} />
                    <Route
                      path="attendance/view/:id"
                      element={<AttendanceDetails />}
                    />
                    <Route
                      path="leave-management"
                      element={<LeaveManagement />}
                    />
                    <Route
                      path="task-management"
                      element={<TaskManagement />}
                    />
                    <Route
                      path="salary-management"
                      element={<SalaryManagement />}
                    />
                    <Route
                      path="salary-management/generate"
                      element={<GenerateSalary />}
                    />
                    <Route
                      path="employee-advance"
                      element={<EmployeeAdvance />}
                    />
                    <Route path="holidays" element={<Holiday />} />
                    <Route
                      path="employee-advance-details/:id"
                      element={<EmployeeAdvanceDetailView />}
                    />
                    <Route
                      path="paysliprequest"
                      element={<PayslipRequests />}
                    />
                    <Route path="id-card" element={<IDCardPage />} />
                  </Route>

                  {/* HR - Additional Routes */}
                  <Route path="calendar/:id" element={<AttendanceCalendar />} />
                  <Route
                    path="travel-allowance"
                    element={<AllowanceCharge />}
                  />
                  <Route path="charge" element={<AllowanceCharge />} />
                  <Route path="employeepayslip" element={<EmployeePayslip />} />

                  {/* ── Clients Module ── */}
                  <Route path="clients" element={<Clients />} />
                  <Route
                    path="clients/details/:id"
                    element={<ClientDetails />}
                  />

                  {/* ── Projects Module ── */}
                  <Route path="projects">
                    <Route index element={<Projects />} />
                    <Route path="add" element={<AddProject />} />
                    <Route path="edit/:id" element={<AddProject />} />
                    <Route path="view/:id" element={<ViewProject />} />
                    <Route path="track/:id" element={<TrackProject />} />
                    <Route path="track" element={<TrackProject />} />
                  </Route>

                  {/* ── Inventory Module ── */}
                  <Route path="inventory">
                    <Route index element={<Navigate to="product" replace />} />
                    <Route path="product" element={<Product />} />
                    <Route path="product/add" element={<AddProduct />} />
                    <Route path="product/edit/:id" element={<AddProduct />} />
                    <Route path="purchase" element={<Purchase />} />
                    <Route
                      path="purchase-return"
                      element={<PurchaseReturn />}
                    />
                    <Route
                      path="purchase-return/add"
                      element={<AddPurchaseReturn />}
                    />
                    <Route
                      path="purchase-return/:id"
                      element={<PurchaseReturnDetails />}
                    />
                    <Route path="purchase/add" element={<AddPurchase />} />
                    <Route path="purchase/edit/:id" element={<AddPurchase />} />
                    <Route path="stock" element={<Stock />} />
                    <Route path="stock-report" element={<StockReport />} />
                    <Route path="damage" element={<Damage />} />
                    <Route path="orders" element={<OrdersList />} />
                    <Route path="orders/:id" element={<OrderDetail />} />
                    <Route path="daily-stock" element={<DailyStockPage />} />
                  </Route>

                  {/* ── Craftsmen Module ── */}
                  <Route path="craftsmen">
                    <Route
                      index
                      element={<Navigate to="craftsmen-master" replace />}
                    />
                    <Route
                      path="craftsmen-master"
                      element={<CarpenterMaster />}
                    />
                    <Route
                      path="craftsmen-master/add"
                      element={<AddCarpenter />}
                    />
                    <Route
                      path="craftsmen-master/view/:id"
                      element={<CarpenterProfile />}
                    />
                    <Route
                      path="craftsmen-master/edit/:id"
                      element={<AddCarpenter />}
                    />
                    <Route
                      path="material-issue"
                      element={<CarpenterMaterialIssue />}
                    />
                    <Route path="attendances" element={<Attendances />} />
                    <Route path="job-assigned" element={<JobAssigned />} />
                    <Route
                      path="work-completion"
                      element={<WorkCompletion />}
                    />
                    <Route
                      path="carpenteradvance"
                      element={<CarpenterAdvanceProfessional />}
                    />
                    <Route
                      path="carpentersalary"
                      element={<CarpenterSalaryManagement />}
                    />
                    <Route
                      path="carpenter-salary/generate"
                      element={<GenerateCarpenterSalary />}
                    />
                    <Route path="agents" element={<Agents />} />
                    <Route path="agents/view/:id" element={<AgentView />} />
                    <Route
                      path="carpenter-advance-details/:id"
                      element={<CarpenterAdvanceDetailView />}
                    />
                  </Route>

                  {/* ── Distributor Module ── */}
                  <Route path="distributor">
                    <Route index element={<Distributor />} />
                    <Route path="add" element={<AddDistributor />} />
                    <Route path="edit/:id" element={<AddDistributor />} />
                    <Route path="view/:id" element={<ViewDistributor />} />
                  </Route>

                  {/* Dealer Redirects (Legacy) */}
                  <Route path="dealer">
                    <Route
                      index
                      element={<Navigate to="/distributor" replace />}
                    />
                    <Route
                      path="add"
                      element={<Navigate to="/distributor/add" replace />}
                    />
                    <Route
                      path="edit/:id"
                      element={<Navigate to="/distributor" replace />}
                    />
                    <Route
                      path="view/:id"
                      element={<Navigate to="/distributor" replace />}
                    />
                  </Route>

                  <Route
                    path="distributor-orders"
                    element={<DistributorOrderHistory />}
                  />
                  <Route path="distributor-orders/add" element={<Addorder />} />
                  <Route path="orders/:id" element={<OrderDetail />} />

                  {/* ── Trader Module ── */}
                  <Route path="trader">
                    <Route index element={<Trader />} />
                    <Route path="add" element={<AddTrader />} />
                    <Route path="edit/:id" element={<AddTrader />} />
                    <Route path="view/:id" element={<ViewTrader />} />
                  </Route>

                  {/* ── Billing Module ── */}
                  <Route path="billing">
                    <Route index element={<Billing />} />
                    <Route path="add" element={<AddBilling />} />
                    <Route path="edit/:id" element={<AddBilling />} />
                    <Route path="invoice/:id" element={<BillingInvoice />} />
                    <Route
                      path="pending-billing"
                      element={<PendingBilling />}
                    />
                    <Route path="balance-sheet" element={<BalanceSheet />} />
                    <Route path="sales-ledger" element={<SalesLedger />} />
                    <Route
                      path="client-sales-history"
                      element={<ClientSalesHistory />}
                    />
                    <Route
                      path="client-sales-history/:id"
                      element={<ClientPaymentHistoryDetails />}
                    />
                    <Route path="sale-return" element={<SaleReturn />} />
                    <Route path="sale-return/add" element={<AddSaleReturn />} />
                    <Route
                      path="sale-return/:id"
                      element={<SaleReturnDetails />}
                    />
                  </Route>

                  {/* ── Reports ── */}
                  <Route path="reports" element={<Reports />} />

                  {/* ── Greetings ── */}
                  <Route path="greetings" element={<Greetings />} />
                  <Route path="poster-editor" element={<PosterEditor />} />

                  {/* ── CCTV Camera ── */}
                  <Route path="camera" element={<HikvisionCamerasPage />} />

                  {/* ── Digital Marketing ── */}
                  <Route
                    path="digitalmarketing"
                    element={<DigitalMarketing />}
                  />

                  {/* ── Transport Management ── */}
                  <Route path="transport">
                    <Route index element={<Navigate to="vehicles" replace />} />

                    {/* Vehicles */}
                    <Route path="vehicles" element={<Vehicles />} />
                    <Route path="vehicles/add" element={<AddVehicle />} />
                    <Route path="vehicles/edit/:id" element={<AddVehicle />} />
                    <Route path="vehicles/view/:id" element={<VehicleView />} />
                    <Route
                      path="vehicles/:id/service-history"
                      element={<ServiceHistory />}
                    />
                    <Route
                      path="vehicles/:vehicleId/service/add"
                      element={<AddEditServiceRecord />}
                    />
                    <Route
                      path="vehicles/:vehicleId/service/edit/:serviceId"
                      element={<AddEditServiceRecord />}
                    />

                    {/* Drivers */}
                    <Route path="drivers" element={<DriverList />} />
                    {/* ✅ Item #22: Mss Name master */}
                    <Route path="mss-name" element={<MssNameList />} />
                    <Route path="drivers/add" element={<AddDriver />} />
                    <Route path="drivers/edit/:id" element={<AddDriver />} />
                    <Route path="drivers/salary" element={<DriverSalary />} />
                    <Route
                      path="drivers/salary/generate"
                      element={<GenerateDriverSalary />}
                    />
                    <Route path="drivers/advance" element={<DriverAdvance />} />
                    <Route
                      path="drivers/advance-details/:id"
                      element={<DriverAdvanceDetailView />}
                    />

                    {/* Locations */}
                    <Route path="locations" element={<LocationList />} />
                    <Route path="locations/add" element={<AddLocation />} />

                    {/* Shipments */}
                    <Route path="shipments" element={<ShipmentList />} />
                    <Route
                      path="shipments/create"
                      element={<CreateShipment />}
                    />
                    <Route
                      path="shipments/history"
                      element={<ShipmentHistory />}
                    />
                    <Route
                      path="shipments/trip/:shipmentId"
                      element={<TripStart />}
                    />
                    <Route
                      path="shipments/live-tracking"
                      element={<LiveTracking />}
                    />
                  </Route>

                  {/* ── Assets & Expenses ── */}
                  <Route path="assets" element={<AssetManagement />} />
                  <Route path="expenses" element={<ExpenseManagement />} />
                  <Route path="expense-type" element={<ExpenseType />} />

                  {/* ── Warehouse ── */}
                  <Route path="warehouse" element={<Warehouse />} />

                  {/* ── Tally ── */}
                  <Route path="tally-sync" element={<TallySync />} />

                  {/* ── 404 Not Found ── */}
                  <Route path="*" element={<ErrorPage notFound />} />
                </Route>
              </Routes>
              </Suspense>
            </RouteErrorBoundary>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </PermissionProvider>
        </PageTitleProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;